import {
  DocumentReferenceResponse,
  useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdThumbnailsQuery,
} from "api/generatedApi";
import { useEffect, useMemo, useState } from "react";
import { documentKindFromName, imageExtensions } from "shared/documents/document-kind";

interface CommonEntryProps {
  key: string;
  altText: string;
  uploadedTimestamp?: string;
}

export type ThumbnailEntry =
  | (CommonEntryProps & {
      kind: "image-with-preview";
      documentId: string;
      imageUrl: string;
    })
  | (CommonEntryProps & {
      kind: "image-no-preview";
      documentId: string;
    })
  | (CommonEntryProps & {
      kind: "pdf";
      documentId: string;
    })
  | (CommonEntryProps & {
      kind: "pending-upload";
      pendingFile: File;
      imageUrl: string;
    });

interface Args {
  projectId: string;
  agreementId: string;
  documents: DocumentReferenceResponse[];
  pendingUploads: File[];
}

interface Result {
  thumbnails: ThumbnailEntry[];
  isLoading: boolean;
}

export const useExtraWorkAgreementThumbnails = ({
  projectId,
  agreementId,
  documents,
  pendingUploads,
}: Args): Result => {
  const skip = !projectId || !agreementId;
  const { data, isLoading } = useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdThumbnailsQuery(
    { projectId, extraWorkAgreementId: agreementId },
    { skip }
  );

  const savedThumbnails = useMemo<ThumbnailEntry[]>(() => {
    const previewByDocumentId = new Map<string, string>();
    for (const t of data?.thumbnails ?? []) {
      if (t.documentId && t.thumbnailDataUri) previewByDocumentId.set(t.documentId, t.thumbnailDataUri);
    }

    const entries: ThumbnailEntry[] = [];
    for (const doc of documents) {
      if (!doc.documentId) continue;
      const altText = doc.name ?? doc.documentId;
      const preview = previewByDocumentId.get(doc.documentId);
      if (preview) {
        entries.push({
          kind: "image-with-preview",
          key: doc.documentId,
          documentId: doc.documentId,
          imageUrl: preview,
          altText,
          uploadedTimestamp: doc.uploadedTimestamp,
        });
        continue;
      }
      const fileKind = documentKindFromName(doc.name);
      if (fileKind === "pdf") {
        entries.push({
          kind: "pdf",
          key: doc.documentId,
          documentId: doc.documentId,
          altText,
          uploadedTimestamp: doc.uploadedTimestamp,
        });
      } else {
        // Treat "image" and "other" the same here: no preview available, full file still reachable via document id.
        entries.push({
          kind: "image-no-preview",
          key: doc.documentId,
          documentId: doc.documentId,
          altText,
          uploadedTimestamp: doc.uploadedTimestamp,
        });
      }
    }
    return entries;
  }, [data, documents]);

  const [pendingPreviews, setPendingPreviews] = useState<ThumbnailEntry[]>([]);
  useEffect(() => {
    const previews: ThumbnailEntry[] = pendingUploads
      .filter((file) => imageExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
      .map((file) => ({
        kind: "pending-upload",
        key: `pending-${file.name}-${file.size}-${file.lastModified}`,
        imageUrl: URL.createObjectURL(file),
        altText: file.name,
        pendingFile: file,
        uploadedTimestamp: new Date(file.lastModified).toISOString(),
      }));
    setPendingPreviews(previews);
    return () => {
      previews.forEach((entry) => {
        if (entry.kind === "pending-upload") URL.revokeObjectURL(entry.imageUrl);
      });
    };
  }, [pendingUploads]);

  return {
    thumbnails: [...savedThumbnails, ...pendingPreviews],
    isLoading,
  };
};
